<template>
  <div>
    <a-popover trigger="click" v-model:open="calendarVisible" placement="bottomLeft">
      <template #content>
        <div class="w-96">
          <a-calendar :fullscreen="false" :value="currentDate" :mode="picker" @select="onSelectDate" @panelChange="onPanelChange">
            <template #dateFullCellRender="{ current }">
              <div :class="getCellClass(current)">
                {{ current.date() }}
              </div>
            </template>
          </a-calendar>
        </div>
      </template>
      <a-select
        v-model:value="formattedDates"
        mode="tags"
        class="w-full"
        :placeholder="placeholder || 'Select multiple dates'"
        @change="onSelectChange"
        @click="openCalendarPicker"
        @focus="openCalendarPicker"
        :options="tagOptions"
        :open="false"
        :allowClear="allowClear"
      />
    </a-popover>
  </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'FormAntdMultipleDatePicker',
  props: {
    /**
     * Value bound via v-model.
     */
    modelValue: {
      type: Array,
      default: () => []
    },
    /**
     * Format to use for dayjs
     */
    format: {
      type: String,
      default: 'M/D/YYYY'
    },
    /**
     * Placeholder.
     */
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * Type to use for datepicker (month | year)
     */
    picker: {
      type: String,
      default: 'month'
    },
    /**
     * Option to determine if selectedDate can be set to null.
     */
    allowClear: {
      type: Boolean,
      default: false
    },
    /**
     * Timezone to use for dayjs
     */
    timezone: {
      type: String,
      default: dayjs.tz.guess()
    },
    /**
     * Open calendar picker.
     */
    open: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'openChange', 'change'],
  data() {
    return {
      calendarVisible: false,
      // selectedDates: [], // raw dayjs objects
      currentDate: dayjs()
    }
  },
  watch: {
    open(val) {
      this.calendarVisible = val
    },
    calendarVisible(val) {
      this.$emit('openChange', val)
    }
  },
  computed: {
    selectedDates: {
      get() {
        return this.modelValue.map((date) => (dayjs.tz(date, this.timezone).isValid() ? dayjs.tz(date, this.timezone) : dayjs(date)))
      },
      set(val) {
        const formattedVal = val.map((d) => d.tz(this.timezone).format(this.format))
        this.$emit('update:modelValue', formattedVal)
        this.$emit('change', formattedVal)
      }
    },
    formattedDates: {
      get() {
        return this.selectedDates.map((d) => d.format('YYYY-MM-DD'))
      },
      set(val) {
        this.selectedDates = val.map((v) => dayjs(v))
      }
    },
    tagOptions() {
      return this.selectedDates.map((d) => ({ label: d.format(this.format), value: d.format('YYYY-MM-DD') }))
    }
  },
  methods: {
    onPanelChange(value, mode) {
      if (mode !== 'date') {
        this.currentDate = value
      }
    },
    onSelectChange(data) {
      if (Array.isArray(data) && data.length === 0) {
        this.selectedDates = data
      }
    },
    onSelectDate(date, data) {
      if (data.source === 'date') {
        const formatted = date.format('YYYY-MM-DD')
        let selectedDates = [...this.selectedDates]
        const index = selectedDates.findIndex((d) => d.format('YYYY-MM-DD') === formatted)
        if (index > -1) {
          selectedDates.splice(index, 1)
        } else {
          selectedDates.push(date)
        }
        this.selectedDates = selectedDates
      } else {
        this.currentDate = date
      }
    },
    openCalendarPicker() {
      this.calendarVisible = true
    },
    closeCalendarPicker() {
      this.calendarVisible = false
    },
    getCellClass(current) {
      const isSelected = this.selectedDates.some((d) => d.isSame(current, 'day'))
      const isCurrentDate = this.currentDate.isSame(current, 'day')
      return `date-cell ${isSelected ? 'date-cell-selected' : isCurrentDate ? 'date-cell-current' : ''}`
    }
  }
}
</script>

<style scoped>
/* Optional styling */
:deep(.ant-picker-calendar-header .ant-picker-calendar-mode-switch) {
  display: none;
}
.date-cell {
  position: relative;
  background-color: #ffffff;
  color: initial;
  border: 1px solid #ffffff;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  margin: 0 auto;
  transition:
    background 0.2s,
    border 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
}
.date-cell:hover {
  background-color: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
}
.date-cell-current,
.date-cell-current:hover {
  border: 1px solid #1677ff;
  background-color: #ffffff;
}
.date-cell-selected,
.date-cell-selected:hover {
  background-color: #1677ff;
  border: 1px solid #1677ff;
  color: #ffffff;
}
</style>
